import { endOfDay, startOfDay } from 'date-fns';
import { fromZonedTime } from 'date-fns-tz';
import moment from 'moment';
import { getTimeZones } from '@vvo/tzdb';

import { getLocalDateTime, getLocalTimeZone } from '@/utils/date';

import { DEFAULT_DATE_FORMAT } from '../contants/common';

export const convertUtcToLocal = (date) => new Date(`${date}Z`).toString();

export const getLabelFromDate = (date, skipLocalConversion = false) => {
  return date ? moment(!skipLocalConversion ? convertUtcToLocal(date) : date).format('MMM DD, YYYY') : '';
};

export const getLabelFromDateTime = (date, is12HoursFormat = true) => {
  return date ? moment(convertUtcToLocal(date)).format(`MMM DD YYYY | ${is12HoursFormat ? 'hh:mm a' : 'HH:mm'}`) : '';
};

export const getDateLabelFormat = (date, is12HoursFormat = true) => {
  return date ? moment(date).format(`MMM DD YYYY | ${is12HoursFormat ? 'hh:mm a' : 'HH:mm'}`) : '';
};

export const getDateLabel = (date) => {
  return date ? moment(date).format(DEFAULT_DATE_FORMAT) : '';
};

export const getPODateLabel = (date) => {
  return date ? moment(date).format(DEFAULT_DATE_FORMAT) : '';
};

export const getTimeLabel = (date) => {
  return date ? moment(date).format('hh:mm:ss A') : '';
};

export const convertTimetoMoment = (time) => (time ? moment(time, 'hh:mm A') : undefined);

export const formateWithMoment = (date, format = 'MM/DD/YYYY', dateStringFormat = false) =>
  date ? moment(date, dateStringFormat).format(format) : '';

export const convertDateStringtoMoment = (time) => (time ? moment(time, 'YYYY-M-DThh:mm:ss') : null);

export const convertDateToMoment = (date, format = 'MM/DD/YYYY') => (date ? moment(date, format) : null);

export const dateTimeToSingleValue = (paramDate, paramTime) => {
  let date = paramDate;
  let time = paramTime;
  if (date) {
    date = date.format('YYYY-MM-DD');
  }
  if (time) {
    time = time.format('HH:mm:ss.SSS');
  } else {
    time = '00:00:00.000';
  }

  if (!time || !date) {
    return null;
  }
  return `${date}T${time}`;
};

export const combineDateTimeMoment = (date = false, time = false) => {
  if (date && time) {
    const dateString = date?.format('DD/MM/YYYY');
    const timeString = time?.format('HH:mm:ss A');
    return moment(`${dateString} ${timeString}`, 'DD/MM/YYYY HH:mm:ss A');
  }
  return null;
};

export const getFormattedTimeString = (date = getLocalDateTime()) => {
  const dateObject = moment(date);
  if (dateObject.isValid()) {
    return `${dateObject.format('YYYY-MM-DDTHH:mm:00')}`;
  }
  return '';
};

export const getFormattedTimeStringWithoutTimeZone = (date = getLocalDateTime()) => moment(date).format();

export const truncateTimeZoneDate = (date) => {
  return date ? moment(date).format('YYYY-MM-DDTHH:mm:ss') : null;
};

export const convertDateESTToUTCToUserLocal = (dateInEST, isTzPrefSupport = true) => {
  if (!dateInEST) {
    return dateInEST;
  }
  const dateInUTC = moment.tz(dateInEST, 'America/New_York').utc();
  const userTimezone = isTzPrefSupport ? getLocalTimeZone() : moment.tz.guess();
  return dateInUTC.tz(userTimezone);
};

export const convertDateUTCToUserLocal = (date, isTzPrefSupport = true) => {
  if (!date) {
    return null;
  }
  const dateInUTC = moment.tz(date, 'UTC');
  const userTimezone = isTzPrefSupport ? getLocalTimeZone() : moment.tz.guess();
  return dateInUTC.tz(userTimezone);
};

export const convertDateUserLocalToUTCToEST = (date) => {
  if (!date) {
    return date;
  }
  const dateInUTC = moment.tz(date, getLocalTimeZone()).utc();
  return dateInUTC.tz('America/New_York');
};

export const convertDateUserLocalToUTC = (date) => {
  if (!date) {
    return date;
  }
  const userTimezone = getLocalTimeZone();
  const localDate = moment.tz(date, 'YYYY-MM-DD', userTimezone);
  return localDate.utc();
};

export const convertedESTToLocalDatesWithUserDateFormat = (date, userDateFormat) => {
  return formateWithMoment(convertDateESTToUTCToUserLocal(date), userDateFormat);
};

export const convertESTToLocalTime = (date) => {
  if (!date) {
    return date;
  }
  const americaTime = moment(date).local().format('YYYY-MM-DDTHH:mm:ss');
  return moment.tz(americaTime, 'America/New_York').tz(getLocalTimeZone());
};

export const convertUserLocalToEST = (date) => moment(date).tz('America/New_York').format('YYYY-MM-DDTHH:mm:ss.SSS');

export const convertLocalToEST = (date) => {
  const userTimezone = getLocalTimeZone();
  const dateInLocal = moment.tz(date, userTimezone);
  const dateInUTC = moment.tz(dateInLocal, 'UTC');
  return dateInUTC.tz('America/New_York');
};

export const convertUserDayTimeToEST = (date, timeOfDay = 'start') => {
  if (!date) {
    return '';
  }
  const localDate = moment(date).local();
  const localDateAtTimeOfDay = timeOfDay === 'start' ? localDate.startOf('day') : localDate.endOf('day');
  const estDate = localDateAtTimeOfDay.tz('America/New_York');
  return estDate.format('YYYY-MM-DDTHH:mm:ss');
};

export const convertUserDayTimeToUTC = (date, timeOfDay) => {
  if (!date) {
    return '';
  }

  const timezone = getLocalTimeZone();

  const baseDate = typeof date === 'string' ? new Date(date) : date;

  const dateAtTime = timeOfDay === 'start' ? startOfDay(baseDate) : endOfDay(baseDate);

  const utcDate = fromZonedTime(dateAtTime, timezone);

  return utcDate.toISOString();
};

export const formatDateToLabel = (dateTime, userDateFormat = DEFAULT_DATE_FORMAT, is12HoursFormat = true, withTime) => {
  if (!dateTime) {
    return dateTime;
  }

  const timeFormat = is12HoursFormat ? 'hh:mm A' : 'HH:mm';
  const format = `${userDateFormat}${withTime ? ` ${timeFormat}` : ''}`;
  return formateWithMoment(dateTime, format);
};

export const convertESTToUserLocalWithFormat = (
  dateInEST,
  userDateFormat = DEFAULT_DATE_FORMAT,
  is12HoursFormat = true,
  withTime,
  sourceFormat = 'MM/DD/YYYY hh:mm:ss A',
) => {
  // TODO: refactor
  // This function behaves similarly to convertDateESTToUTCToUserLocal,
  // but with additional formatting and is specifically for the MM/DD/YYYY hh:mm:ss A format in EST.
  if (!dateInEST) {
    return dateInEST;
  }
  const dateInUTC = moment.tz(dateInEST, sourceFormat, 'America/New_York').utc();
  const userTimezone = getLocalTimeZone();
  const timeFormat = is12HoursFormat ? 'hh:mm A' : 'HH:mm';
  const format = `${userDateFormat}${withTime ? ` ${timeFormat}` : ''}`;
  return formateWithMoment(dateInUTC.tz(userTimezone), format);
};

export const getTimeZonesFromIANA = () => {
  const timeZones = getTimeZones({ includeUtc: true });
  return timeZones.map((zone) => ({
    countryCode: zone.countryCode,
    label: `${zone.currentTimeFormat} [${zone.abbreviation}]`,
    value: zone.name,
  }));
};

export const mergeDateAndTimeObjectsWithTimeZone = (date, time, timeZone) => {
  const newDate = date ? date.clone() : undefined;
  if (newDate && time && timeZone) {
    newDate.set({
      hour: time.get('hours'),
      minute: time.get('minute'),
      second: '00',
    });
    const newShipDate = moment.tz(newDate.format('YYYY-MM-DDTHH:mm:ss.SSS'), timeZone);
    return newShipDate.utc();
  }
  return null;
};
